/**
 * IrelandSchoolsDirectory — EEI Accredited Schools Directory
 * Shows all English Education Ireland (EEI) member schools with:
 * - Search by name, city, program
 * - Filter by city, program type, and 6-month fee range
 * - 6-month total tuition fees (26 weeks × weekly rate)
 * - Morning / Afternoon class distinction
 */
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, MapPin, GraduationCap, ChevronDown, ChevronUp,
  ExternalLink, Shield, BookOpen, Clock, Users, Star, Euro
} from "lucide-react";

export interface EEISchool {
  id: string;
  name: string;
  city: string;
  description: string;
  programs: string[];
  morningFeeWeekly?: number;   // weekly rate in EUR (number for calculation)
  afternoonFeeWeekly?: number; // weekly rate in EUR (number for calculation)
  registrationFee: string;
  insuranceFee: string;
  examFee?: string;
  examName?: string;
  website?: string;
  accreditation: string[];
  features: string[];
  eeiUrl: string;
}

// 6 months = 26 weeks
const WEEKS = 26;

function fmt6mo(weeklyEur: number): string {
  const total = weeklyEur * WEEKS;
  return `€${total.toLocaleString()}`;
}

const EEI_SCHOOLS: EEISchool[] = [
  // ── Dublin ─────────────────────────────────────────────────────
  {
    id: "isi-dublin",
    name: "ISI Dublin",
    city: "Dublin",
    description: "Creative, design-focused school offering quality English programmes with immersive cultural and academic experiences. EEI member and Trinity College London exam centre.",
    programs: ["Ерөнхий англи хэл", "Академик жилийн хөтөлбөр", "Эрчимжүүлсэн англи хэл", "Мэргэжлийн англи хэл"],
    morningFeeWeekly: 144,
    afternoonFeeWeekly: 115,
    registrationFee: "€100",
    insuranceFee: "€140/жил",
    examFee: "€220",
    examName: "Oxford Test of English",
    website: "https://studyinireland.ie",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Өглөөний анги 09:00–12:15", "Өдрийн анги 13:00–17:00", "Ажиллах эрхтэй хөтөлбөр", "Монгол хэлний дэмжлэг"],
    eeiUrl: "https://englisheducationireland.ie/schools/isi-dublin/",
  },
  {
    id: "international-house-dublin",
    name: "International House Dublin",
    city: "Dublin",
    description: "Дублиний хамгийн алдартай хэлний сургуулиудын нэг. Олон төрлийн хөтөлбөр, оршин суух байр, урт хугацааны чанарын баталгаатай.",
    programs: ["Ерөнхий англи хэл", "Бизнесийн англи хэл", "Шалгалтын бэлтгэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 175,
    afternoonFeeWeekly: 140,
    registrationFee: "€80",
    insuranceFee: "€150/жил",
    examFee: "€200",
    examName: "IELTS / Cambridge",
    website: "https://ihdublin.com",
    accreditation: ["EEI", "ACELS", "Eaquals"],
    features: ["Өглөөний анги", "Өдрийн анги", "Байрны хэрэгцээ хангана", "Нийгмийн хөтөлбөр"],
    eeiUrl: "https://englisheducationireland.ie/schools/international-house-dublin/",
  },
  {
    id: "irish-college-of-english",
    name: "Irish College of English",
    city: "Dublin",
    description: "Далайн эрэгт байрлах шагналт хэлний сургууль. 1992 оноос хойш насанд хүрэгчид болон залуучуудад зориулсан хөтөлбөр явуулж байна.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Залуучуудын хөтөлбөр", "Гэр бүлийн байрны хөтөлбөр"],
    morningFeeWeekly: 160,
    afternoonFeeWeekly: 130,
    registrationFee: "€75",
    insuranceFee: "€145/жил",
    examFee: "€190",
    examName: "Cambridge / IELTS",
    website: "https://www.iceireland.com",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Далайн эрэгт байрлалтай", "Гэр бүлийн байрны хөтөлбөр", "Насанд хүрэгчид ба залуучуудын анги"],
    eeiUrl: "https://englisheducationireland.ie/schools/irish-college-of-english/",
  },
  {
    id: "kaplan-international",
    name: "Kaplan International",
    city: "Dublin",
    description: "Дэлхий даяар алдартай Kaplan сүлжээний Дублин дахь сургууль. Орчин үеийн заах арга, олон улсын орчин.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "IELTS бэлтгэл", "Бизнесийн англи хэл"],
    morningFeeWeekly: 180,
    afternoonFeeWeekly: 145,
    registrationFee: "€90",
    insuranceFee: "€155/жил",
    examFee: "€210",
    examName: "IELTS",
    website: "https://www.kaplaninternational.com",
    accreditation: ["EEI", "ACELS"],
    features: ["Олон улсын сүлжээ", "Орчин үеийн байгууламж", "Карьерын зөвлөгөө"],
    eeiUrl: "https://englisheducationireland.ie/schools/kaplan-international/",
  },
  {
    id: "eli-schools-dublin",
    name: "ELI Schools – Dublin",
    city: "Dublin",
    description: "2017 онд байгуулагдсан, Ирландын хамгийн том хэлний сургуулиудын нэг болж өссөн. 2023 онд салбарынхаа шилдэгүүдийн нэгд нэрлэгдсэн.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Шалгалтын бэлтгэл", "Академик жил"],
    morningFeeWeekly: 155,
    afternoonFeeWeekly: 125,
    registrationFee: "€80",
    insuranceFee: "€140/жил",
    examFee: "€195",
    examName: "IELTS / Cambridge",
    website: "https://elischools.ie",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Хотын төвд байрлалтай", "Мэргэжлийн багш нар", "Оюутны дэмжлэгийн үйлчилгээ"],
    eeiUrl: "https://englisheducationireland.ie/schools/eli-schools-dublin/",
  },
  {
    id: "ces-dublin",
    name: "Centre of English Studies (CES) – Dublin",
    city: "Dublin",
    description: "1979 оноос хойш үйл ажиллагаа явуулж буй CES нь хотын төвд орчин үеийн байгууламжтай, идэвхтэй нийгмийн хөтөлбөртэй.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "IELTS бэлтгэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 165,
    afternoonFeeWeekly: 135,
    registrationFee: "€85",
    insuranceFee: "€145/жил",
    examFee: "€200",
    examName: "IELTS",
    website: "https://ces-schools.com",
    accreditation: ["EEI", "ACELS", "Eaquals"],
    features: ["1979 оноос хойш", "Хотын төвд", "Идэвхтэй нийгмийн хөтөлбөр"],
    eeiUrl: "https://englisheducationireland.ie/schools/centre-of-english-studies-ces-dublin/",
  },
  {
    id: "future-learning-dublin",
    name: "Future Learning – Dublin",
    city: "Dublin",
    description: "Уламжлалт бус, технологид суурилсан сургууль. Дижитал медиа бүтээх дамжуулан англи хэлийг заадаг. Eaquals-ийн хамгийн өндөр зэрэглэлтэй.",
    programs: ["Ерөнхий англи хэл", "Технологи + Англи хэл", "Бизнесийн англи хэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 170,
    afternoonFeeWeekly: 140,
    registrationFee: "€90",
    insuranceFee: "€150/жил",
    examFee: "€205",
    examName: "Cambridge",
    website: "https://futurelearning.ie",
    accreditation: ["EEI", "Eaquals"],
    features: ["Технологид суурилсан заах арга", "Дижитал медиа бүтээлч хөтөлбөр", "Хамтын ажиллагааны суралцахуй"],
    eeiUrl: "https://englisheducationireland.ie/schools/future-learning-dublin/",
  },
  {
    id: "everest-language-school",
    name: "Everest Language School",
    city: "Dublin",
    description: "2015 онд байгуулагдсан, оюутны хувь хүний суралцах хэв маягт анхаарал хандуулдаг шинэлэг хандлагатай сургууль.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Шалгалтын бэлтгэл"],
    morningFeeWeekly: 150,
    afternoonFeeWeekly: 120,
    registrationFee: "€70",
    insuranceFee: "€140/жил",
    examFee: "€190",
    examName: "IELTS / Cambridge",
    website: "https://everestlanguageschool.ie",
    accreditation: ["EEI", "ACELS"],
    features: ["Хувь хүний суралцах хэв маягт тохирсон", "Жижиг ангийн хэмжээ", "Хотын төвд"],
    eeiUrl: "https://englisheducationireland.ie/schools/everest-language-school/",
  },
  {
    id: "atlas-language-schools-dublin",
    name: "Atlas Language Schools – Dublin",
    city: "Dublin",
    description: "Оюутны хэлний аяллын хөтөлбөрийн бүх талд хамгийн өндөр түвшний үйлчилгээ үзүүлдэг. Байр, нийгмийн хөтөлбөр, хэлний анги бүгдийг хамарна.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Бизнесийн англи хэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 165,
    afternoonFeeWeekly: 135,
    registrationFee: "€80",
    insuranceFee: "€145/жил",
    examFee: "€200",
    examName: "IELTS",
    website: "https://www.atlas-language-school.com",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Байрны хэрэгцээ хангана", "Нийгмийн хөтөлбөр", "Нисэх онгоцны буудлаас угтах үйлчилгээ"],
    eeiUrl: "https://englisheducationireland.ie/schools/atlas-language-schools-dublin/",
  },
  {
    id: "swan-training-institute",
    name: "Swan Training Institute",
    city: "Dublin",
    description: "Trinity College-ийн хажууд байрлах, насанд хүрэгчид болон залуучуудад зориулсан өндөр чанарын хөтөлбөртэй урт хугацааны туршлагатай сургууль.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "IELTS бэлтгэл", "Бизнесийн англи хэл"],
    morningFeeWeekly: 175,
    afternoonFeeWeekly: 140,
    registrationFee: "€85",
    insuranceFee: "€150/жил",
    examFee: "€210",
    examName: "IELTS / Cambridge",
    website: "https://swantraining.ie",
    accreditation: ["EEI", "ACELS", "Eaquals"],
    features: ["Trinity College-ийн хажууд", "Насанд хүрэгчид ба залуучуудын хөтөлбөр", "Урт хугацааны туршлага"],
    eeiUrl: "https://englisheducationireland.ie/schools/swan-training-institute/",
  },
  {
    id: "ec-dublin",
    name: "EC Dublin",
    city: "Dublin",
    description: "Дублин хотын хөгжилтэй нийслэлд суралцах боломж. Тайван уур амьсгал, соёлын баялаг туршлагаараа алдартай.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "IELTS бэлтгэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 170,
    afternoonFeeWeekly: 138,
    registrationFee: "€80",
    insuranceFee: "€145/жил",
    examFee: "€200",
    examName: "IELTS",
    website: "https://www.ecenglish.com/en/locations/ireland/dublin",
    accreditation: ["EEI", "ACELS"],
    features: ["Хотын төвд", "Соёлын хөтөлбөр", "Олон улсын орчин"],
    eeiUrl: "https://englisheducationireland.ie/schools/ec-dublin/",
  },
  {
    id: "dcu-international-academy",
    name: "Dublin City University International Academy",
    city: "Dublin",
    description: "Их сургуулийн нэг хэсэг болох энэ академи нь их сургуульд бэлтгэх болон мэргэжлийн амжилтад чиглэсэн академик болон ерөнхий англи хэлний хөтөлбөр санал болгодог.",
    programs: ["Академик англи хэл", "Ерөнхий англи хэл", "Их сургуульд бэлтгэх хөтөлбөр", "Мэргэжлийн англи хэл"],
    morningFeeWeekly: 185,
    afternoonFeeWeekly: 150,
    registrationFee: "€100",
    insuranceFee: "€155/жил",
    examFee: "€220",
    examName: "IELTS / Cambridge",
    website: "https://www.dcu.ie/international",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Их сургуулийн кампус", "Академик хөтөлбөр", "Их сургуульд шилжих боломж"],
    eeiUrl: "https://englisheducationireland.ie/schools/dublin-city-university-international-academy/",
  },
  {
    id: "apollo-language-centre",
    name: "Apollo Language Centre",
    city: "Dublin",
    description: "Grafton Street-ийн ойролцоо байрлах Eaquals & IALC баталгаажсан сургууль. Насанд хүрэгчид болон залуучуудад зориулсан өндөр чанарын хөтөлбөр.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Залуучуудын хөтөлбөр", "Бизнесийн англи хэл"],
    morningFeeWeekly: 168,
    afternoonFeeWeekly: 135,
    registrationFee: "€85",
    insuranceFee: "€148/жил",
    examFee: "€200",
    examName: "Cambridge",
    website: "https://apollolanguagecentre.com",
    accreditation: ["EEI", "Eaquals", "IALC"],
    features: ["Grafton Street-ийн ойролцоо", "Eaquals & IALC баталгаажсан", "Насанд хүрэгчид ба залуучуудын хөтөлбөр"],
    eeiUrl: "https://englisheducationireland.ie/schools/apollo-language-centre/",
  },
  {
    id: "atc-language-schools-dublin",
    name: "ATC Language Schools – Dublin",
    city: "Dublin",
    description: "ACELS, Eaquals баталгаажсан, Quality English гишүүн. Дублин болон Брэй хотод жилийн турш насанд хүрэгчдийн сургуультай.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "IELTS бэлтгэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 162,
    afternoonFeeWeekly: 130,
    registrationFee: "€80",
    insuranceFee: "€145/жил",
    examFee: "€195",
    examName: "IELTS",
    website: "https://atclanguageschools.com",
    accreditation: ["EEI", "ACELS", "Eaquals"],
    features: ["Дублин ба Брэй хотод", "Жилийн туршид нээлттэй", "Олон улсын нэр хүнд"],
    eeiUrl: "https://englisheducationireland.ie/schools/atc-language-schools-dublin/",
  },
  {
    id: "babel-academy",
    name: "Babel Academy of English",
    city: "Dublin",
    description: "O'Connell Bridge болон Capel Street дээр хоёр байршилтай. Зун дахь сургуулийн хөтөлбөр болон гэр бүлийн байрны үйлчилгээтэй.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Зуны хөтөлбөр"],
    morningFeeWeekly: 150,
    afternoonFeeWeekly: 120,
    registrationFee: "€70",
    insuranceFee: "€140/жил",
    examFee: "€185",
    examName: "Cambridge",
    website: "https://babelacademy.ie",
    accreditation: ["EEI", "ACELS"],
    features: ["Хоёр байршилтай", "Гэр бүлийн байрны үйлчилгээ", "Зуны хөтөлбөр"],
    eeiUrl: "https://englisheducationireland.ie/schools/babel-academy-of-english/",
  },
  {
    id: "ulearn",
    name: "ULearn English Language School",
    city: "Dublin",
    description: "St. Stephen's Green-ийн хажууд байрлах, үнэ цэнэ болон уян хатан байдлаараа алдартай туршлагатай сургууль.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Шалгалтын бэлтгэл"],
    morningFeeWeekly: 148,
    afternoonFeeWeekly: 118,
    registrationFee: "€70",
    insuranceFee: "€138/жил",
    examFee: "€185",
    examName: "IELTS",
    website: "https://ulearn.ie",
    accreditation: ["EEI", "ACELS"],
    features: ["St. Stephen's Green-ийн хажууд", "Уян хатан цагийн хуваарь", "Боломжийн үнэ"],
    eeiUrl: "https://englisheducationireland.ie/schools/ulearn-english-language-school/",
  },
  // ── Cork ────────────────────────────────────────────────────────
  {
    id: "ces-cork",
    name: "Centre of English Studies (CES) – Cork",
    city: "Cork",
    description: "45 гаруй жилийн туршлагатай, Georgian загварын кампустай, кафетери, лаборатори, CELTA баталгаатай багш нартай.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Залуучуудын хөтөлбөр", "IELTS бэлтгэл"],
    morningFeeWeekly: 155,
    afternoonFeeWeekly: 125,
    registrationFee: "€80",
    insuranceFee: "€145/жил",
    examFee: "€195",
    examName: "IELTS",
    website: "https://ces-schools.com",
    accreditation: ["EEI", "ACELS", "Eaquals"],
    features: ["45+ жилийн туршлага", "Georgian загварын кампус", "CELTA баталгаатай багш нар"],
    eeiUrl: "https://englisheducationireland.ie/schools/centre-of-english-studies-ces-cork/",
  },
  {
    id: "cork-english-academy",
    name: "Cork English Academy",
    city: "Cork",
    description: "Корк хотын төвд байрлах бие даасан сургууль. Өндөр чанарын англи хэлний сургалт, шалгалтын бэлтгэл, бүлгийн аялалын хөтөлбөр.",
    programs: ["Ерөнхий англи хэл", "Шалгалтын бэлтгэл", "Бүлгийн аялалын хөтөлбөр"],
    morningFeeWeekly: 150,
    afternoonFeeWeekly: 120,
    registrationFee: "€75",
    insuranceFee: "€140/жил",
    examFee: "€190",
    examName: "IELTS / Cambridge",
    website: "https://corkEnglishacademy.ie",
    accreditation: ["EEI", "ACELS"],
    features: ["Хотын төвд", "Бие даасан сургууль", "Шалгалтын бэлтгэлд мэргэшсэн"],
    eeiUrl: "https://englisheducationireland.ie/schools/cork-english-academy/",
  },
  {
    id: "cec-cork",
    name: "CEC – Cork English College",
    city: "Cork",
    description: "1978 оноос хойш үйл ажиллагаа явуулж буй гэр бүлийн эзэмшлийн сургууль. ACELS болон EEI гишүүн.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 148,
    afternoonFeeWeekly: 118,
    registrationFee: "€70",
    insuranceFee: "€138/жил",
    examFee: "€185",
    examName: "Cambridge",
    website: "https://cecireland.com",
    accreditation: ["EEI", "ACELS"],
    features: ["1978 оноос хойш", "Гэр бүлийн эзэмшлийн сургууль", "Хувийн анхаарал"],
    eeiUrl: "https://englisheducationireland.ie/schools/cec-cork-english-college/",
  },
  {
    id: "ucc-language-centre",
    name: "University College Cork Language Centre",
    city: "Cork",
    description: "Их сургуулийн кампуст суурилсан хэлний төв. Академик хөтөлбөрт чиглэсэн, дэмжлэгтэй орчинтой.",
    programs: ["Академик англи хэл", "Ерөнхий англи хэл", "Их сургуульд бэлтгэх хөтөлбөр"],
    morningFeeWeekly: 175,
    afternoonFeeWeekly: 145,
    registrationFee: "€95",
    insuranceFee: "€150/жил",
    examFee: "€210",
    examName: "IELTS",
    website: "https://www.ucc.ie/en/languagecentre/",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Их сургуулийн кампус", "Академик хөтөлбөр", "Их сургуульд шилжих боломж"],
    eeiUrl: "https://englisheducationireland.ie/schools/university-college-cork-language-centre/",
  },
  // ── Galway ──────────────────────────────────────────────────────
  {
    id: "bridge-mills-galway",
    name: "Bridge Mills Galway Language Centre",
    city: "Galway",
    description: "1987 оноос хойш үйл ажиллагаа явуулж буй гэр бүлийн эзэмшлийн сургууль. 18-р зууны тээрмийн барилгад байрлах, Corrib голын эрэг дээр. EAQUALS, ACELS, ALTO, QQI баталгаажсан.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Бизнесийн англи хэл", "IELTS бэлтгэл"],
    morningFeeWeekly: 160,
    afternoonFeeWeekly: 130,
    registrationFee: "€80",
    insuranceFee: "€145/жил",
    examFee: "€200",
    examName: "IELTS",
    website: "https://bridgemills.ie",
    accreditation: ["EEI", "Eaquals", "ACELS", "ALTO", "QQI"],
    features: ["18-р зууны тээрмийн барилга", "Голын эрэг дээр", "Гэр бүлийн эзэмшлийн сургууль", "Олон баталгаажилттай"],
    eeiUrl: "https://englisheducationireland.ie/schools/bridge-mills-galway-language-centre-international-house/",
  },
  {
    id: "elc-university-galway",
    name: "English Language Centre, University of Galway",
    city: "Galway",
    description: "Галуэй их сургуулийн хэлний төв. 19,000 гаруй оюутантай их сургуулийн кампуст суурилсан.",
    programs: ["Академик англи хэл", "Ерөнхий англи хэл", "Их сургуульд бэлтгэх хөтөлбөр"],
    morningFeeWeekly: 180,
    afternoonFeeWeekly: 148,
    registrationFee: "€100",
    insuranceFee: "€155/жил",
    examFee: "€215",
    examName: "IELTS",
    website: "https://www.universityofgalway.ie/english-language-centre/",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Их сургуулийн кампус", "Академик хөтөлбөр", "Галуэй хотын төвд"],
    eeiUrl: "https://englisheducationireland.ie/schools/english-language-centre-university-of-galway/",
  },
  // ── Limerick ────────────────────────────────────────────────────
  {
    id: "limerick-language-centre",
    name: "Limerick Language Centre",
    city: "Limerick",
    description: "30 гаруй жилийн туршлагатай, 40 гаруй улсаас оюутан хүлээн авдаг найрсаг гэр бүлийн сургууль.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Шалгалтын бэлтгэл"],
    morningFeeWeekly: 145,
    afternoonFeeWeekly: 115,
    registrationFee: "€70",
    insuranceFee: "€138/жил",
    examFee: "€185",
    examName: "Cambridge / IELTS",
    website: "https://limericklanguagecentre.ie",
    accreditation: ["EEI", "ACELS"],
    features: ["30+ жилийн туршлага", "40+ улсаас оюутан", "Гэр бүлийн найрсаг уур амьсгал"],
    eeiUrl: "https://englisheducationireland.ie/schools/limerick-language-centre/",
  },
  {
    id: "rightword-institute",
    name: "Rightword Institute",
    city: "Limerick",
    description: "Лимерикийн хотын төвд Crescent Street дээр байрлах орчин үеийн хэлний сургууль.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Бизнесийн англи хэл"],
    morningFeeWeekly: 142,
    afternoonFeeWeekly: 112,
    registrationFee: "€70",
    insuranceFee: "€135/жил",
    examFee: "€180",
    examName: "Cambridge",
    website: "https://rightword.ie",
    accreditation: ["EEI", "ACELS"],
    features: ["Хотын төвд", "Орчин үеийн байгууламж", "Ирландын 3-р том хотод"],
    eeiUrl: "https://englisheducationireland.ie/schools/rightword-institute/",
  },
  {
    id: "ul-language-centre",
    name: "University of Limerick Language Centre",
    city: "Limerick",
    description: "UL их сургуулийн нэг хэсэг болох хэлний төв. Их сургуульд орохоор бэлтгэж буй болон олон улсын зочдод зориулсан хөтөлбөр.",
    programs: ["Академик англи хэл", "Ерөнхий англи хэл", "Мэргэжлийн англи хэл"],
    morningFeeWeekly: 170,
    afternoonFeeWeekly: 140,
    registrationFee: "€90",
    insuranceFee: "€150/жил",
    examFee: "€205",
    examName: "IELTS",
    website: "https://www.ul.ie/languagecentre",
    accreditation: ["EEI", "ACELS", "QQI"],
    features: ["Их сургуулийн кампус", "Академик хөтөлбөр"],
    eeiUrl: "https://englisheducationireland.ie/schools/university-of-limerick-language-centre/",
  },
  // ── Killarney ───────────────────────────────────────────────────
  {
    id: "killarney-school-of-english",
    name: "Killarney School of English",
    city: "Killarney",
    description: "Киллярни үндэсний цэцэрлэгт хүрээлэнгийн ойролцоо байрлах гэр бүлийн эзэмшлийн сургууль. Байгальд суурилсан туршлага.",
    programs: ["Ерөнхий англи хэл", "Залуучуудын хөтөлбөр", "Эрчимжүүлсэн англи хэл"],
    morningFeeWeekly: 148,
    afternoonFeeWeekly: 118,
    registrationFee: "€70",
    insuranceFee: "€138/жил",
    examFee: "€185",
    examName: "Cambridge",
    website: "https://killarneylanguage.com",
    accreditation: ["EEI", "ACELS"],
    features: ["Үндэсний цэцэрлэгт хүрээлэнгийн ойролцоо", "Байгальд суурилсан туршлага", "Гэр бүлийн найрсаг"],
    eeiUrl: "https://englisheducationireland.ie/schools/killarney-school-of-english/",
  },
  // ── Athlone ─────────────────────────────────────────────────────
  {
    id: "future-learning-athlone",
    name: "Future Learning – Athlone",
    city: "Athlone",
    description: "Дублин болон Атлон хотод жилийн туршид нээлттэй, таван зуны төвтэй. Eaquals-ийн хамгийн өндөр зэрэглэлтэй.",
    programs: ["Ерөнхий англи хэл", "Технологи + Англи хэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 155,
    afternoonFeeWeekly: 125,
    registrationFee: "€85",
    insuranceFee: "€142/жил",
    examFee: "€195",
    examName: "Cambridge",
    website: "https://futurelearning.ie",
    accreditation: ["EEI", "Eaquals"],
    features: ["Ирландын төвд байрлалтай", "Дублин ба Атлон хотод", "Eaquals баталгаажсан"],
    eeiUrl: "https://englisheducationireland.ie/schools/future-learning-athlone/",
  },
  // ── Donegal ─────────────────────────────────────────────────────
  {
    id: "donegal-english-language-school",
    name: "Donegal English Language School",
    city: "Donegal",
    description: "Шагналт сургууль. Ирландын далайн эрэгт байгальд суурилсан туршлагатай англи хэлний сургалт.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "Залуучуудын хөтөлбөр"],
    morningFeeWeekly: 140,
    afternoonFeeWeekly: 110,
    registrationFee: "€65",
    insuranceFee: "€135/жил",
    examFee: "€180",
    examName: "Cambridge",
    website: "https://donegallanguageschool.com",
    accreditation: ["EEI", "ACELS"],
    features: ["Далайн эрэгт", "Байгалийн гайхамшигт орчин", "Шагналт сургууль"],
    eeiUrl: "https://englisheducationireland.ie/schools/donegal-english-language-school/",
  },
  // ── Bray ────────────────────────────────────────────────────────
  {
    id: "atc-language-schools-bray",
    name: "ATC Language Schools – Bray",
    city: "Bray",
    description: "Дублин хотоос ойрхон Брэй хотод байрлах. ACELS, Eaquals баталгаажсан, Quality English гишүүн.",
    programs: ["Ерөнхий англи хэл", "Эрчимжүүлсэн англи хэл", "IELTS бэлтгэл"],
    morningFeeWeekly: 155,
    afternoonFeeWeekly: 125,
    registrationFee: "€78",
    insuranceFee: "€142/жил",
    examFee: "€192",
    examName: "IELTS",
    website: "https://atclanguageschools.com",
    accreditation: ["EEI", "ACELS", "Eaquals"],
    features: ["Дублиний ойролцоо", "Далайн эрэгт хот", "Жилийн туршид нээлттэй"],
    eeiUrl: "https://englisheducationireland.ie/schools/atc-language-schools-bray/",
  },
];

const CITIES = ["Бүх хот", "Dublin", "Cork", "Galway", "Limerick", "Killarney", "Athlone", "Donegal", "Bray"];

const ALL_PROGRAMS = [
  "Бүх хөтөлбөр",
  "Ерөнхий англи хэл",
  "Эрчимжүүлсэн англи хэл",
  "Академик англи хэл",
  "Бизнесийн англи хэл",
  "IELTS бэлтгэл",
  "Залуучуудын хөтөлбөр",
  "Академик жилийн хөтөлбөр",
];

// Fee range options based on 6-month morning class total
const FEE_RANGES = [
  { label: "Бүх үнэ", min: 0, max: Infinity },
  { label: "€3,500 хүртэл", min: 0, max: 3500 },
  { label: "€3,500 – €4,000", min: 3500, max: 4000 },
  { label: "€4,000 – €4,500", min: 4000, max: 4500 },
  { label: "€4,500 дээш", min: 4500, max: Infinity },
];

function SchoolCard({ school }: { school: EEISchool }) {
  const [expanded, setExpanded] = useState(false);

  const morning6mo = school.morningFeeWeekly ? school.morningFeeWeekly * WEEKS : null;
  const afternoon6mo = school.afternoonFeeWeekly ? school.afternoonFeeWeekly * WEEKS : null;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="rounded-2xl border border-[#e2e8f0] bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1E6FD9] bg-blue-50 px-2 py-0.5 rounded-full">
                <MapPin className="w-3 h-3" />
                {school.city}
              </span>
              {school.accreditation.includes("Eaquals") && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full">
                  <Star className="w-3 h-3" />
                  Eaquals
                </span>
              )}
            </div>
            <h3 className="font-bold text-[#0A1628] text-base leading-snug" style={{ fontFamily: "var(--font-display)" }}>
              {school.name}
            </h3>
          </div>
          {/* 6-month fee summary in header */}
          <div className="flex-shrink-0 text-right">
            {morning6mo && (
              <div className="mb-1">
                <div className="text-[10px] text-gray-400">Өглөөний анги / 6 сар</div>
                <div className="text-base font-black text-[#1E6FD9]">{fmt6mo(school.morningFeeWeekly!)}</div>
              </div>
            )}
            {afternoon6mo && (
              <div>
                <div className="text-[10px] text-gray-400">Өдрийн анги / 6 сар</div>
                <div className="text-sm font-bold text-gray-600">{fmt6mo(school.afternoonFeeWeekly!)}</div>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-600 leading-relaxed mb-3" style={{ fontFamily: "var(--font-body)" }}>
          {school.description}
        </p>

        {/* Programs */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {school.programs.slice(0, 3).map((p) => (
            <span key={p} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">
              {p}
            </span>
          ))}
          {school.programs.length > 3 && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              +{school.programs.length - 3}
            </span>
          )}
        </div>

        {/* Quick fee summary */}
        <div className="grid grid-cols-3 gap-2 mb-3">
          <div className="text-center p-2 rounded-xl bg-[#f8fafc]">
            <div className="text-[10px] text-gray-500 mb-0.5">Бүртгэлийн хураамж</div>
            <div className="text-xs font-bold text-gray-800">{school.registrationFee}</div>
          </div>
          <div className="text-center p-2 rounded-xl bg-[#f8fafc]">
            <div className="text-[10px] text-gray-500 mb-0.5">Эрүүл мэндийн даатгал</div>
            <div className="text-xs font-bold text-gray-800">{school.insuranceFee}</div>
          </div>
          <div className="text-center p-2 rounded-xl bg-[#f8fafc]">
            <div className="text-[10px] text-gray-500 mb-0.5">Шалгалтын хураамж</div>
            <div className="text-xs font-bold text-gray-800">{school.examFee || "—"}</div>
          </div>
        </div>

        {/* Expand button */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between text-xs font-semibold text-[#1E6FD9] hover:text-[#1558b0] transition-colors py-1"
          style={{ fontFamily: "var(--font-body)" }}
        >
          <span>{expanded ? "Дэлгэрэнгүй хаах" : "Дэлгэрэнгүй харах"}</span>
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-[#f1f5f9]"
          >
            <div className="p-4 space-y-4 bg-[#f8fafc]">
              {/* Detailed fee table — 6-month totals */}
              <div>
                <h4 className="text-xs font-bold text-[#0A1628] mb-2 flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-[#1E6FD9]" />
                  Сургалтын төлбөр — 6 сарын нийт дүн (26 долоо хоног)
                </h4>
                <div className="rounded-xl overflow-hidden border border-[#e2e8f0]">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#1E6FD9] text-white">
                        <th className="text-left px-3 py-2 font-semibold">Ангийн төрөл</th>
                        <th className="text-left px-3 py-2 font-semibold">Цагийн хуваарь</th>
                        <th className="text-right px-3 py-2 font-semibold">7 хоног/€</th>
                        <th className="text-right px-3 py-2 font-semibold">6 сар нийт</th>
                      </tr>
                    </thead>
                    <tbody>
                      {school.morningFeeWeekly && (
                        <tr className="bg-white border-b border-[#f1f5f9]">
                          <td className="px-3 py-2.5 font-semibold text-gray-800">
                            <span className="inline-flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
                              Өглөөний анги
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500">09:00–12:15</td>
                          <td className="px-3 py-2.5 text-right text-gray-600">€{school.morningFeeWeekly}</td>
                          <td className="px-3 py-2.5 text-right font-black text-[#1E6FD9]">{fmt6mo(school.morningFeeWeekly)}</td>
                        </tr>
                      )}
                      {school.afternoonFeeWeekly && (
                        <tr className="bg-white">
                          <td className="px-3 py-2.5 font-semibold text-gray-800">
                            <span className="inline-flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                              Өдрийн анги
                            </span>
                          </td>
                          <td className="px-3 py-2.5 text-gray-500">13:00–17:00</td>
                          <td className="px-3 py-2.5 text-right text-gray-600">€{school.afternoonFeeWeekly}</td>
                          <td className="px-3 py-2.5 text-right font-black text-emerald-600">{fmt6mo(school.afternoonFeeWeekly)}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5 pl-1">* 26 долоо хоног × 7 хоногийн үнэ = 6 сарын нийт сургалтын төлбөр</p>
              </div>

              {/* Visa-related fees */}
              <div>
                <h4 className="text-xs font-bold text-[#0A1628] mb-2 flex items-center gap-1.5">
                  <Shield className="w-3.5 h-3.5 text-[#16a34a]" />
                  Виз мэдүүлэхэд шаардлагатай нэмэлт төлбөрүүд
                </h4>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-white border border-[#e2e8f0]">
                    <span className="text-xs text-gray-600">Бүртгэлийн хураамж (сургуульд)</span>
                    <span className="text-xs font-bold text-gray-800">{school.registrationFee}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-white border border-[#e2e8f0]">
                    <span className="text-xs text-gray-600">Эрүүл мэндийн даатгал</span>
                    <span className="text-xs font-bold text-gray-800">{school.insuranceFee}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-white border border-[#e2e8f0]">
                    <span className="text-xs text-gray-600">Суралцагчийн хамгааллын даатгал</span>
                    <span className="text-xs font-bold text-gray-800">~€40–€60/жил</span>
                  </div>
                  {school.examFee && (
                    <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-white border border-[#e2e8f0]">
                      <span className="text-xs text-gray-600">{school.examName} шалгалтын хураамж</span>
                      <span className="text-xs font-bold text-gray-800">{school.examFee}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-1.5 px-3 rounded-lg bg-amber-50 border border-amber-200">
                    <span className="text-xs text-amber-700 font-medium">IRP бүртгэлийн хураамж (ирсний дараа)</span>
                    <span className="text-xs font-bold text-amber-800">€300</span>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h4 className="text-xs font-bold text-[#0A1628] mb-2 flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5 text-[#7c3aed]" />
                  Онцлог
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {school.features.map((f) => (
                    <span key={f} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 font-medium border border-purple-100">
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              {/* Accreditation */}
              <div>
                <h4 className="text-xs font-bold text-[#0A1628] mb-2 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-[#d97706]" />
                  Баталгаажилт
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {school.accreditation.map((a) => (
                    <span key={a} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold border border-amber-200">
                      {a}
                    </span>
                  ))}
                </div>
              </div>

              {/* Links */}
              <div className="flex gap-2">
                {school.website && (
                  <a
                    href={school.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-white bg-[#1E6FD9] hover:bg-[#1558b0] transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Сургуулийн вэбсайт
                  </a>
                )}
                <a
                  href={school.eeiUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold text-[#1E6FD9] bg-blue-50 hover:bg-blue-100 transition-colors border border-blue-200"
                >
                  <GraduationCap className="w-3.5 h-3.5" />
                  EEI хуудас
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function IrelandSchoolsDirectory() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCity, setSelectedCity] = useState("Бүх хот");
  const [selectedProgram, setSelectedProgram] = useState("Бүх хөтөлбөр");
  const [selectedFeeRange, setSelectedFeeRange] = useState(0); // index into FEE_RANGES

  const filteredSchools = useMemo(() => {
    const feeRange = FEE_RANGES[selectedFeeRange];
    return EEI_SCHOOLS.filter((school) => {
      const matchesSearch =
        searchQuery === "" ||
        school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
        school.programs.some((p) => p.toLowerCase().includes(searchQuery.toLowerCase())) ||
        school.description.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCity = selectedCity === "Бүх хот" || school.city === selectedCity;

      const matchesProgram =
        selectedProgram === "Бүх хөтөлбөр" ||
        school.programs.some((p) => p === selectedProgram);

      // Fee filter: based on 6-month morning class total
      const morning6mo = school.morningFeeWeekly ? school.morningFeeWeekly * WEEKS : 0;
      const matchesFee = morning6mo >= feeRange.min && morning6mo < feeRange.max;

      return matchesSearch && matchesCity && matchesProgram && matchesFee;
    });
  }, [searchQuery, selectedCity, selectedProgram, selectedFeeRange]);

  const hasFilters = selectedCity !== "Бүх хот" || selectedProgram !== "Бүх хөтөлбөр" || searchQuery || selectedFeeRange !== 0;

  return (
    <div className="py-10">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <GraduationCap className="w-5 h-5 text-[#1E6FD9]" />
          <span className="text-xs font-bold tracking-widest uppercase text-[#1E6FD9]" style={{ fontFamily: "var(--font-body)" }}>
            English Education Ireland (EEI)
          </span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-black text-[#0A1628] mb-2" style={{ fontFamily: "var(--font-display)" }}>
          EEI Бүртгэлтэй Сургуулиуд
        </h2>
        <p className="text-gray-500 text-sm max-w-2xl" style={{ fontFamily: "var(--font-body)" }}>
          English Education Ireland нь Ирландын хамгийн том болон хамгийн урт хугацаанд үйл ажиллагаа явуулж буй
          хэлний боловсролын холбоо юм. Доорх сургуулиуд бүгд EEI гишүүн бөгөөд Ирландын виз мэдүүлэхэд
          зөвшөөрөгдсөн байгууллагууд юм. Сургалтын төлбөр нь <strong>6 сарын (26 долоо хоног)</strong> нийт дүнгээр харагдана.
        </p>
      </div>

      {/* Search and filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {/* Search */}
        <div className="relative sm:col-span-2 lg:col-span-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Нэр, хот, хөтөлбөрөөр хайх..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E6FD9] focus:border-transparent"
            style={{ fontFamily: "var(--font-body)" }}
          />
        </div>

        {/* City filter */}
        <select
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E6FD9] cursor-pointer"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {CITIES.map((city) => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>

        {/* Program filter */}
        <select
          value={selectedProgram}
          onChange={(e) => setSelectedProgram(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E6FD9] cursor-pointer"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {ALL_PROGRAMS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>

        {/* Fee range filter */}
        <div className="relative">
          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <select
            value={selectedFeeRange}
            onChange={(e) => setSelectedFeeRange(Number(e.target.value))}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E6FD9] cursor-pointer"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {FEE_RANGES.map((range, i) => (
              <option key={range.label} value={i}>{range.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500" style={{ fontFamily: "var(--font-body)" }}>
          <strong className="text-[#0A1628]">{filteredSchools.length}</strong> сургууль олдлоо
          {hasFilters && (
            <button
              onClick={() => { setSearchQuery(""); setSelectedCity("Бүх хот"); setSelectedProgram("Бүх хөтөлбөр"); setSelectedFeeRange(0); }}
              className="ml-2 text-[#1E6FD9] hover:underline text-xs"
            >
              Шүүлт цэвэрлэх
            </button>
          )}
        </span>
        <span className="text-xs text-gray-400">Нийт {EEI_SCHOOLS.length} сургууль • 6 сарын үнэ</span>
      </div>

      {/* School cards grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <AnimatePresence>
          {filteredSchools.length > 0 ? (
            filteredSchools.map((school) => (
              <SchoolCard key={school.id} school={school} />
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full text-center py-16 text-gray-400"
            >
              <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Хайлтад тохирох сургууль олдсонгүй</p>
              <button
                onClick={() => { setSearchQuery(""); setSelectedCity("Бүх хот"); setSelectedProgram("Бүх хөтөлбөр"); setSelectedFeeRange(0); }}
                className="mt-2 text-[#1E6FD9] text-xs hover:underline"
              >
                Шүүлт цэвэрлэх
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Fee info note */}
      <div className="mt-8 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-800" style={{ fontFamily: "var(--font-body)" }}>
        <strong>⚠️ Анхааруулга:</strong> Дээрх төлбөрийн мэдээлэл нь ойролцоо тооцоо бөгөөд сургуулийн вэбсайтаас эцсийн баталгааг авна уу.
        Виз мэдүүлэхэд шаардлагатай нийт дүнд <strong>сургалтын төлбөр (6 сар) + эрүүл мэндийн даатгал + бүртгэлийн хураамж + шалгалтын хураамж + IRP бүртгэл (€300)</strong> орно.
      </div>
    </div>
  );
}
